import React, { lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, TrendingDown, Scale, Repeat, Smartphone, BrainCircuit } from 'lucide-react';
// Optimized Unsplash images: auto=format (webp/avif), moderate quality, cropped.
const cover = (id: string) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=600&h=400&q=60`;
const painPoints = [{
  icon: <MessageCircle className="w-6 h-6" data-id="element-145" />,
  img: cover('1584515933487-779824d29309'),
  alt: 'Dermatólogo respondiendo mensajes de noche',
  title: 'Contestás WhatsApp a las 11 de la noche',
  description: 'La misma pregunta, la número 15 de la semana, sin ningún filtro que la frene antes de llegar a vos.'
}, {
  icon: <TrendingDown className="w-6 h-6" data-id="element-146" />,
  img: cover('1631217868264-e5b90bb7e133'),
  alt: 'Paciente que abandona su tratamiento dermatológico',
  title: 'Tus pacientes abandonan a mitad de camino',
  description: 'Nadie les explicó qué esperar en la semana 3, cuando el acné empeora antes de mejorar.'
}, {
  icon: <Scale className="w-6 h-6" data-id="element-147" />,
  img: cover('1629909613654-28e377c37b09'),
  alt: 'Procedimiento estético con duda legal',
  title: 'Hacés un peeling y te queda la duda legal',
  description: 'Sin consentimiento firmado como corresponde, cualquier complicación normal se puede convertir en un problema serio.'
}, {
  icon: <Repeat className="w-6 h-6" data-id="element-148" />,
  img: cover('1576091160550-2173dba999ef'),
  alt: 'Dermatóloga repitiendo la misma indicación',
  title: 'Repetís la misma indicación en cada consulta',
  description: 'Explicás fotoprotección, cuidados básicos y dudas de siempre, una y otra vez, sin nada que delegar.'
}, {
  icon: <Smartphone className="w-6 h-6" data-id="element-149" />,
  img: cover('1512428559087-560fa5ceab42'),
  alt: 'El consultorio persigue al dermatólogo fuera de horario',
  title: 'Tu consultorio te sigue a todos lados',
  description: 'Cenás con tu familia y el celular vibra. Estás de vacaciones y contestás igual.'
}, {
  icon: <BrainCircuit className="w-6 h-6" data-id="element-150" />,
  img: cover('1587560699334-cc4ff634909a'),
  alt: 'Dermatólogo sin un sistema de seguimiento',
  title: 'No tenés un sistema, tenés memoria',
  description: 'Todo el seguimiento de tus pacientes vive en tu cabeza, no en un proceso que se pueda sostener solo.'
}];
export function PainPoints() {
  return <section className="py-12 md:py-24 bg-brand-light" data-id="element-151">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-152">
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-16" data-id="element-153">
          <h2 className="text-xs md:text-sm font-bold text-brand-gold uppercase tracking-widest mb-2 md:mb-3" data-id="element-154">
            Realidad de Consultorio
          </h2>
          <h3 className="text-2xl md:text-4xl font-serif font-bold text-brand-navy mb-3 md:mb-6" data-id="element-155">
            ¿Te pasa esto?
          </h3>
          <p className="text-base md:text-lg text-slate-600" data-id="element-156">
            ¿Cuántas consultas más vas a sostener sin un sistema que respalde
            cada indicación?
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8" data-id="element-157">
          {painPoints.map((point, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} whileHover={{
          y: -6
        }} transition={{
          duration: 0.4,
          delay: index * 0.05
        }} className="group bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl hover:border-brand-gold/40 transition-all cursor-default overflow-hidden" data-id="element-158">
              <div className="relative h-40 md:h-44 overflow-hidden" data-id="element-159">
                <img src={point.img} alt={point.alt} loading="lazy" decoding="async" width={600} height={400} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-160" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-brand-navy/10 to-transparent" data-id="element-161" />
                <div className="absolute bottom-3 left-3 w-11 h-11 rounded-xl bg-brand-gold text-brand-navy flex items-center justify-center shadow-lg" data-id="element-162">
                  {point.icon}
                </div>
              </div>
              <div className="p-5 md:p-6" data-id="element-163">
                <h4 className="text-lg md:text-xl font-bold text-slate-800 mb-2 leading-tight" data-id="element-164">
                  {point.title}
                </h4>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed" data-id="element-165">
                  {point.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}